/* eslint-disable no-undef */

import React, { useState, useEffect, useCallback } from "react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import axios from 'axios';
import { Modal, Button, Upload, Alert, Progress, Row, Col, Statistic, Divider, List, Card, Tooltip, Descriptions, Tag, Avatar, Image, Space } from 'antd';
import { UploadOutlined, DownloadOutlined, CheckCircleOutlined, CloseCircleOutlined, InfoCircleOutlined, LoadingOutlined, TeamOutlined, UserOutlined, ClockCircleOutlined, StopOutlined, UserOutlined as UserIcon, PhoneOutlined, MailOutlined, HomeOutlined, IdcardOutlined, BankOutlined, FileProtectOutlined, EditOutlined, PrinterOutlined } from '@ant-design/icons';

import CardComponent from "../../components/cards/CardComponent";
import AddNewEmployeeForm from "../../components/hr/addNewEmployeeForm";
import EmployeeDataTable from "../../components/hr/EmployeeDataTable";
import Header from "../../components/Header";
import Layout from "../../layout/Layout";
import MaterialButton from "../../components/Buttons/MaterialButton";
import fetchData from "../../functions/fetchData";
import { getHeaders, getAuthToken } from "../../utility/getHeader";
import "../../components/hr/EmployeePhotoStyles.css";
import logo from '../../assets/logo.png';

export default function EmployeeMaster() {
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadResults, setUploadResults] = useState(null);
  const [statusFilter, setStatusFilter] = useState('All'); // New filter state
  const [statistics, setStatistics] = useState({
    total: 0,
    active: 0,
    probation: 0,
    inactive: 0
  });
  
  // Multi-selection state for bulk PDF export
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  
  // Duplicate field tracking
  const [duplicateFields, setDuplicateFields] = useState([]);
  const [autoGeneratedFields, setAutoGeneratedFields] = useState({});
  
  // Apply filter to employees
  const applyFilter = useCallback((employeeList, filter) => {
    let filtered = employeeList;
    
    if (filter !== 'All') {
      filtered = employeeList.filter(emp => emp.status === filter);
    }
    
    setFilteredEmployees(filtered);
  }, []);

  // Handle status filter change
  const handleStatusFilter = (status) => {
    setStatusFilter(status);
    applyFilter(employees, status);
  };
  
  // Multi-selection handlers
  const handleSelectEmployee = (employee, isSelected) => {
    if (isSelected) {
      setSelectedEmployees(prev => [...prev, employee]);
    } else {
      setSelectedEmployees(prev => prev.filter(emp => emp.id !== employee.id));
    }
  };

  const handleSelectAll = (isSelected) => {
    setSelectAll(isSelected);
    if (isSelected) {
      setSelectedEmployees([...filteredEmployees]);
    } else {
      setSelectedEmployees([]);
    }
  };

  // Reset selections when filter changes
  useEffect(() => {
    setSelectedEmployees([]);
    setSelectAll(false);
  }, [statusFilter, filteredEmployees]);
  
  // Load employees from API
  const loadEmployees = useCallback(async () => {
    setLoading(true);
    try {
      // Call API to get employees
      const employeesData = await fetchEmployees();
      setEmployees(employeesData);
      
      // Calculate statistics
      const stats = {
        total: employeesData.length,
        active: employeesData.filter(emp => emp.status === 'Active').length,
        probation: employeesData.filter(emp => emp.status === 'Probation').length,
        inactive: 0
      };
      stats.inactive = stats.total - stats.active - stats.probation; // Calculate inactive
      setStatistics(stats);
      
      // Apply current filter
      applyFilter(employeesData, statusFilter);
      
    } catch (error) {
      console.error('Error loading employees:', error);
      
      // Show specific API error messages to user
      let errorMessage = 'Failed to load employees';
      
      if (error.message.includes('Authentication failed')) {
        errorMessage = 'Session expired. Please login again.';
        // Optionally redirect to login page
      } else if (error.message.includes('Network error')) {
        errorMessage = 'Cannot connect to server. Please check your internet connection.';
      } else if (error.message.includes('Server error')) {
        errorMessage = 'Server is experiencing issues. Please try again later.';
      } else {
        errorMessage = error.message || 'An unexpected error occurred while loading employees.';
      }
      
      toast.error(errorMessage);
      
      // Set empty array and reset statistics on error
      setEmployees([]);
      setFilteredEmployees([]);
      setStatistics({
        total: 0,
        active: 0,
        probation: 0,
        inactive: 0
      });
    } finally {
      setLoading(false);
    }
  }, [statusFilter, applyFilter]);

  // Function to fetch employees from API
  const fetchEmployees = async () => {
    try {
      // Use fetchData method to get employees from database
      const employees = await fetchData("employees", null, "id", {});
      
      console.log('Employees loaded successfully:', employees);
      
      // Transform the data to ensure profile_photo_url is properly formatted
      const transformedEmployees = (employees || []).map(emp => ({
        ...emp,
        profile_photo_url: emp.profile_photo ? 
          `http://localhost:4402/uploads/${emp.profile_photo}` : 
          null
      }));
      
      return transformedEmployees;
    } catch (error) {
      console.error('Error fetching employees:', error);
      
      // Provide detailed error information
      if (error.response) {
        // Server responded with error status
        const status = error.response.status;
        const errorData = error.response.data;
        
        console.error('API Error Response:', {
          status,
          data: errorData,
          headers: error.response.headers
        });
        
        // Create specific error message based on status code
        let errorMessage = 'Failed to fetch employees';
        
        switch (status) {
          case 401:
            errorMessage = 'Authentication failed. Please login again.';
            break;
          case 403:
            errorMessage = 'Access denied. You do not have permission to view employees.';
            break;
          case 404:
            errorMessage = 'Employee API endpoint not found. Please check server configuration.';
            break;
          case 500:
            errorMessage = 'Server error occurred while fetching employees.';
            break;
          default:
            errorMessage = errorData?.msg || errorData?.message || `Server error (${status})`;
        }
        
        const apiError = new Error(errorMessage);
        apiError.response = error.response;
        throw apiError;
      } else if (error.request) {
        // Network error - no response received
        console.error('Network Error:', error.request);
        throw new Error('Network error: Unable to connect to the server. Please check your internet connection.');
      } else {
        // Something else happened
        console.error('Error:', error.message);
        throw new Error(`Request error: ${error.message}`);
      }
    }
  };

  // Load employees on component mount
  useEffect(() => {
    loadEmployees();
  }, [loadEmployees]);

  // Handler functions for employee actions
  const handleViewEmployeeDetails = (employee) => {
    console.log('Viewing employee details:', employee);
    setSelectedEmployee(employee);
    setShowDetailModal(true);
  };

  const handleEditEmployee = (employee) => {
    console.log('Editing employee:', employee);
    setEditingEmployee(employee);
    setShowAddForm(true);
    const fullName = `${employee.english_first_name || ''} ${employee.english_last_name || ''}`.trim();
    toast.info(`Editing ${fullName}`);
  };

  const handleSaveEmployee = async (employeeData) => {
    try {
      setLoading(true);
      
      if (editingEmployee) {
        // Update existing employee
        await updateEmployee(editingEmployee.employee_id, employeeData);
        toast.success('Employee updated successfully!');
      } else {
        // Create new employee
        console.log('Saving new employee data:', employeeData);
        // const response = await employeeService.createEmployee(employeeData);
        toast.success('Employee saved successfully!');
      }
      
      setShowAddForm(false);
      setEditingEmployee(null);
      setRefreshTrigger(prev => prev + 1); // Trigger refresh of EmployeeDataTable
      await loadEmployees(); // Reload statistics
    } catch (error) {
      console.error('Error saving employee:', error);
      
      // Extract and display specific error message
      let errorMessage = editingEmployee ? 'Failed to update employee' : 'Failed to save employee';
      
      if (error.message) {
        // Use the formatted error message from updateEmployee function
        errorMessage = error.message;
      } else if (error.response) {
        if (error.response.data.error) {
          errorMessage = `Database Error: ${error.response.data.error}`;
        } else if (error.response.data.msg) {
          errorMessage = error.response.data.msg;
        } else if (error.response.data.message) {
          errorMessage = error.response.data.message;
        }
      }
      
      // Only show toast if it wasn't already shown in updateEmployee
      if (!error.message || !error.message.includes('Database Error')) {
        toast.error(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  // Function to update employee using the backend API
  const updateEmployee = async (employeeId, employeeData) => {
    try {
      // Prepare data object for new API
      const updateData = {};
      
      // Map form fields to database column names
      const fieldMapping = {
        // Personal Information
        'first_name_th': 'thai_first_name',
        'last_name_th': 'thai_last_name',
        'first_name_en': 'english_first_name',
        'last_name_en': 'english_last_name',
        'national_id': 'thai_id_number',
        'address': 'current_address',
        
        // Employment & Salary
        'position': 'position_id',
        'department': 'department_id',
        'salary': 'salary_amount',
        'basic_salary': 'salary_amount',
        'contract_type': 'employment_type',
        'employment_status': 'status',
        'contract_start_date': 'hire_date',
        'contract_end_date': 'probation_end_date',
        
        // Bank Information
        'account_number': 'bank_account_number',
        'account_name': 'bank_account_name',
        'branch_name': 'bank_branch',
        
        // Contact Information  
        'line_id': 'line_id',
        
        // Visa/Work Permit
        'visa_expiry_date': 'visa_expiry',
        'work_permit_expiry_date': 'work_permit_expiry',
        
        // Address details
        'current_sub_district': 'current_sub_district',
        'current_district': 'current_district',
        'current_province': 'current_province',
        'current_postal_code': 'current_postal_code',
        'permanent_address': 'permanent_address',
        'permanent_province': 'permanent_province',
        'permanent_postal_code': 'permanent_postal_code',
        
        // Additional fields
        'thai_nickname': 'thai_nickname',
        'english_nickname': 'english_nickname',
        'place_of_birth': 'place_of_birth',
        'religion': 'religion',
        'social_security_number': 'social_security_number',
        'tax_id': 'tax_id'
      };

      // Fields to exclude from database update
      const excludeFields = [
        'profile_photo',
        'profile_photo_preview',
        'full_name_en',
        'full_name_th',
        'allowances',
        'images'
      ];

      // Build update data object
      Object.keys(employeeData).forEach(key => {
        if (employeeData[key] !== null && employeeData[key] !== undefined && employeeData[key] !== '') {
          // Skip excluded fields
          if (excludeFields.includes(key)) {
            console.log(`Excluding field: ${key}`);
            return;
          }
          
          // Use mapped field name or original key
          const dbFieldName = fieldMapping[key] || key;
          
          // Handle contract_type to employment_type mapping
          let value = employeeData[key];
          if (key === 'contract_type') {
            if (value === 'Fixed-term' || value === 'Temporary') value = 'Contract';
            else if (value === 'Part-time') value = 'Part-time';
            else value = 'Full-time';
          }
          
          // Handle position/department name to ID conversion
          if (key === 'position' || key === 'department') {
            if (isNaN(value)) {
              if (key === 'position') {
                const positionMapping = {
                  'Chef': 1,
                  'Sous Chef': 2,
                  'Cook': 3,
                  'Waitress': 4,
                  'Waiter': 4,
                  'Cashier': 5,
                  'Manager': 6,
                  'Cleaner': 7,
                  'Security': 8,
                  'Accountant': 9
                };
                value = positionMapping[value] || 1;
              } else if (key === 'department') {
                const departmentMapping = {
                  'Kitchen': 1,
                  'Service': 2,
                  'Management': 3,
                  'Cleaning': 4,
                  'Security': 5,
                  'Accounting': 6
                };
                value = departmentMapping[value] || 1;
              }
              console.log(`Mapped ${key} "${employeeData[key]}" to ID: ${value}`);
            }
          }
          
          updateData[dbFieldName] = value;
        }
      });

      console.log('Update data to send:', updateData);
      console.log('Update URL:', `/updatedata/employees/${employeeId}`);
      console.log('Update data:', JSON.stringify(updateData, null, 2));

      const response = await axios.put(
        `/updatedata/employees/${employeeId}`,
        updateData,
        getHeaders()
      );

      console.log('Employee updated successfully:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error updating employee:', error);
      
      let errorMessage = 'Failed to update employee';
      const duplicates = [];
      const generated = {};
      
      if (error.response) {
        console.error('Error response:', error.response.data);
        
        // Check for duplicate key errors
        const errorText = error.response.data?.error || error.response.data?.msg || error.response.data?.message || '';
        const isDuplicateError = errorText.toLowerCase().includes('duplicate') || 
                                errorText.toLowerCase().includes('unique') ||
                                errorText.includes('ER_DUP_ENTRY');
        
        if (isDuplicateError) {
          // Parse which field has duplicate
          let duplicateField = null;
          
          // Common patterns for duplicate errors
          if (errorText.includes('employee_id')) {
            duplicateField = 'employee_id';
            generated.employee_id = `EMP${Date.now().toString().slice(-6)}`;
          } else if (errorText.includes('email')) {
            duplicateField = 'email';
            generated.email = `employee${Date.now()}@example.com`;
          } else if (errorText.includes('phone')) {
            duplicateField = 'phone_number';
            generated.phone_number = `08${Math.floor(10000000 + Math.random() * 90000000)}`;
          } else if (errorText.includes('thai_id_number') || errorText.includes('national_id')) {
            duplicateField = 'thai_id_number';
            generated.thai_id_number = `${Math.floor(1000000000000 + Math.random() * 9000000000000)}`;
          } else if (errorText.includes('passport_number')) {
            duplicateField = 'passport_number';
            generated.passport_number = `P${Date.now().toString().slice(-8)}`;
          } else if (errorText.includes('social_security_number')) {
            duplicateField = 'social_security_number';
            generated.social_security_number = `SS${Date.now().toString().slice(-9)}`;
          } else if (errorText.includes('bank_account_number')) {
            duplicateField = 'bank_account_number';
            generated.bank_account_number = `${Math.floor(1000000000 + Math.random() * 9000000000)}`;
          }
          
          if (duplicateField) {
            duplicates.push(duplicateField);
            setDuplicateFields(duplicates);
            setAutoGeneratedFields(generated);
            
            errorMessage = `Duplicate value detected in field: ${duplicateField.replace(/_/g, ' ').toUpperCase()}.\n\nSuggested value: ${generated[duplicateField]}.\n\nPlease update the highlighted field and try again.`;
            toast.error(errorMessage, { autoClose: 8000 });
          } else {
            errorMessage = `Duplicate entry detected: ${errorText}`;
            toast.error(errorMessage);
          }
          
          // Throw error with duplicate info
          const duplicateError = new Error(errorMessage);
          duplicateError.duplicates = duplicates;
          duplicateError.generated = generated;
          throw duplicateError;
        }
        
        // Handle different types of errors
        if (error.response.status === 401) {
          errorMessage = 'Authentication failed. Please login again.';
          toast.error(errorMessage);
        } else if (error.response.status === 400) {
          // Bad request - usually validation or SQL errors
          if (error.response.data.error) {
            errorMessage = `Database Error: ${error.response.data.error}`;
          } else if (error.response.data.msg) {
            errorMessage = error.response.data.msg;
          } else if (error.response.data.message) {
            errorMessage = error.response.data.message;
          }
          toast.error(errorMessage);
        } else if (error.response.status === 500) {
          // Server error
          if (error.response.data.error) {
            errorMessage = `Server Error: ${error.response.data.error}`;
          } else {
            errorMessage = 'Internal server error occurred';
          }
          toast.error(errorMessage);
        } else {
          // Other HTTP errors
          if (error.response.data.msg) {
            errorMessage = error.response.data.msg;
          } else if (error.response.data.message) {
            errorMessage = error.response.data.message;
          } else if (error.response.data.error) {
            errorMessage = error.response.data.error;
          } else {
            errorMessage = `Error ${error.response.status}: ${error.response.statusText}`;
          }
          toast.error(errorMessage);
        }
        
        throw new Error(errorMessage);
      } else if (error.request) {
        // Network error
        errorMessage = 'Network error: Unable to connect to server';
        toast.error(errorMessage);
        throw new Error(errorMessage);
      } else {
        // Other errors
        errorMessage = error.message || 'An unexpected error occurred';
        toast.error(errorMessage);
        throw new Error(errorMessage);
      }
    }
  };

  // Export functions
  const exportToPDF = () => {
    try {
      const doc = new jsPDF('l', 'mm', 'a4'); // Landscape orientation
      
      // Add company header
      doc.setFontSize(20);
      doc.setFont(undefined, 'bold');
      doc.text('JANNAAT HR MANAGEMENT SYSTEM', 14, 22);
      
      doc.setFontSize(16);
      doc.setFont(undefined, 'normal');
      doc.text('Employee Directory Report', 14, 32);
      
      // Add report metadata
      doc.setFontSize(11);
      const currentDate = new Date();
      const dateStr = currentDate.toLocaleDateString('en-GB');
      const timeStr = currentDate.toLocaleTimeString('en-GB');
      
      doc.text(`Generated on: ${dateStr} at ${timeStr}`, 14, 42);
      doc.text(`Total Records: ${employees.length}`, 14, 48);
      doc.text(`Report Filter: ${employees.length === statistics.total ? 'All Employees' : 'Filtered Results'}`, 14, 54);
      
      // Add statistics summary
      doc.setFontSize(12);
      doc.setFont(undefined, 'bold');
      doc.text('Summary Statistics:', 200, 42);
      doc.setFontSize(10);
      doc.setFont(undefined, 'normal');
      doc.text(`Active: ${statistics.active}`, 200, 48);
      doc.text(`Probation: ${statistics.probation}`, 200, 52);
      doc.text(`Inactive: ${statistics.inactive}`, 200, 56);
      doc.text(`Total: ${statistics.total}`, 200, 60);
      
      // Prepare enhanced table data with Thai language support
      const tableHeaders = [
        'Emp ID', 'English Name', 'Position', 
        'Department', 'Hire Date', 'Status', 'Phone', 'Email'
      ];
      
      const tableData = employees.map(emp => {
        const englishName = `${emp.english_first_name || ''} ${emp.english_last_name || ''}`.trim();
        
        return [
          emp.employee_id || 'N/A',
          englishName || 'N/A',
          emp.position_name || emp.position_id || 'N/A',
          emp.department_name || emp.department_id || 'N/A',
          emp.hire_date ? new Date(emp.hire_date).toLocaleDateString('en-GB') : 'N/A',
          emp.status || 'N/A',
          emp.phone_number || 'N/A',
          emp.email || 'N/A'
        ];
      });

      // Add table with enhanced styling
      doc.autoTable({
        head: [tableHeaders],
        body: tableData,
        startY: 70,
        styles: { 
          fontSize: 8,
          cellPadding: 2,
          halign: 'left',
          valign: 'middle'
        },
        headStyles: { 
          fillColor: [0, 123, 255], // Bootstrap primary blue
          textColor: [255, 255, 255],
          fontStyle: 'bold',
          halign: 'center'
        },
        columnStyles: {
          0: { cellWidth: 25, halign: 'center' }, // Employee ID
          1: { cellWidth: 45 }, // English Name
          2: { cellWidth: 35 }, // Position
          3: { cellWidth: 35 }, // Department
          4: { cellWidth: 25, halign: 'center' }, // Hire Date
          5: { cellWidth: 20, halign: 'center' }, // Status
          6: { cellWidth: 30, halign: 'center' }, // Phone
          7: { cellWidth: 40 } // Email
        },
        alternateRowStyles: {
          fillColor: [248, 249, 250] // Light gray for alternate rows
        },
        tableLineColor: [200, 200, 200],
        tableLineWidth: 0.1,
        margin: { top: 70, left: 14, right: 14 },
        // Add header to each page
        didDrawPage: function(data) {
          // Only add header if this is not the first page
          if (data.pageNumber > 1) {
            // Add company header
            doc.setFontSize(20);
            doc.setFont(undefined, 'bold');
            doc.text('JANNAAT HR MANAGEMENT SYSTEM', 14, 22);
            
            doc.setFontSize(16);
            doc.setFont(undefined, 'normal');
            doc.text('Employee Directory Report (Continued)', 14, 32);
            
            // Add page indicator
            doc.setFontSize(11);
            doc.text(`Page ${data.pageNumber} - Continued from previous page`, 14, 42);
            
            // Add separator line
            doc.setLineWidth(0.5);
            doc.line(14, 50, 285, 50);
          }
        }
      });

      // Add detailed footer with additional information
      const finalY = doc.lastAutoTable.finalY + 10;
      
      // Add separator line
      doc.setLineWidth(0.5);
      doc.line(14, finalY, 285, finalY);
      
      doc.setFontSize(10);
      doc.setFont(undefined, 'bold');
      doc.text('Report Summary:', 14, finalY + 8);
      
      doc.setFont(undefined, 'normal');
      doc.text(`• Total Employees: ${statistics.total}`, 14, finalY + 15);
      doc.text(`• Active Employees: ${statistics.active} (${((statistics.active/statistics.total)*100).toFixed(1)}%)`, 14, finalY + 21);
      doc.text(`• Employees on Probation: ${statistics.probation} (${((statistics.probation/statistics.total)*100).toFixed(1)}%)`, 14, finalY + 27);
      doc.text(`• Inactive Employees: ${statistics.inactive} (${((statistics.inactive/statistics.total)*100).toFixed(1)}%)`, 14, finalY + 33);
      
      // Add page number
      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.text(`Page ${i} of ${pageCount}`, 280, 200, { align: 'right' });
        doc.text(`Generated by Jannaat HR System - ${dateStr}`, 14, 200);
      }
      
      // Generate and download file
      const fileName = `jannaat_employees_report_${new Date().toISOString().split('T')[0]}.pdf`;
      doc.save(fileName);
      
      toast.success('Enhanced PDF report downloaded successfully!');
    } catch (error) {
      console.error('Error exporting to PDF:', error);
      toast.error('Failed to export PDF file');
    }
  };

  // Bulk export selected employees' detailed profiles to PDF
  const exportSelectedEmployeesToPDF = async () => {
    if (selectedEmployees.length === 0) {
      toast.error('Please select at least one employee to export');
      return;
    }

    try {
      toast.info('Generating PDF... Please wait.');
      const doc = new jsPDF('portrait', 'mm', 'a4');
      let yPosition = 20;
      
      for (let i = 0; i < selectedEmployees.length; i++) {
        const employee = selectedEmployees[i];
        
        if (i > 0) {
          doc.addPage();
          yPosition = 20; // Reset position for new page
        }
        
        // Add company header (matching individual export format)
        doc.setFontSize(16);
        doc.setFont(undefined, 'bold');
        doc.text('JANNAAT HR MANAGEMENT SYSTEM', 105, yPosition, { align: 'center' });
        yPosition += 6;
        
        doc.setFontSize(12);
        doc.setFont(undefined, 'normal');
        doc.text('Employee Information Report', 105, yPosition, { align: 'center' });
        yPosition += 5;
        
        doc.setFontSize(9);
        const currentDate = new Date();
        doc.text(`Generated on: ${currentDate.toLocaleDateString('en-GB')} at ${currentDate.toLocaleTimeString('en-GB')}`, 105, yPosition, { align: 'center' });
        yPosition += 5;
        
        // Add separator line
        doc.setLineWidth(0.5);
        doc.line(20, yPosition, 190, yPosition);
        yPosition += 6;
        
        // Try to add profile photo on the right side (matching individual export)
        let profileImageAdded = false;
        
        if (employee.profile_photo_url || employee.profile_photo) {
          try {
            // Build the correct image URL
            let imageUrl = null;
            
            if (employee.profile_photo_url) {
              imageUrl = employee.profile_photo_url;
            } else if (employee.profile_photo) {
              // Handle different path formats
              if (employee.profile_photo.startsWith('http')) {
                imageUrl = employee.profile_photo;
              } else if (employee.profile_photo.startsWith('/uploads/') || employee.profile_photo.startsWith('uploads/')) {
                imageUrl = `http://localhost:4402/${employee.profile_photo.startsWith('/') ? employee.profile_photo.substring(1) : employee.profile_photo}`;
              } else {
                imageUrl = `http://localhost:4402/uploads/${employee.profile_photo}`;
              }
            }
            
            if (imageUrl) {
              const imageData = await getImageAsBase64(imageUrl);
              doc.addImage(imageData, 'JPEG', 165, 42, 20, 25);
              profileImageAdded = true;
            }
          } catch (error) {
            console.error('Failed to load profile photo for PDF:', error);
          }
        }
        
        // Add dummy profile image if no photo or failed to load
        if (!profileImageAdded) {
          const dummyImage = getDummyProfileImage();
          doc.addImage(dummyImage, 'JPEG', 165, 42, 20, 25);
        }
        
        // Employee basic information with optimized spacing
        const fullName = `${employee.english_first_name || ''} ${employee.english_last_name || ''}`.trim();
        const thaiFullName = `${employee.thai_first_name || ''} ${employee.thai_last_name || ''}`.trim();
        
        doc.setFontSize(14);
        doc.setFont(undefined, 'bold');
        doc.text(fullName, 20, yPosition);
        yPosition += 6;
        
        if (thaiFullName) {
          doc.setFontSize(11);
          doc.setFont(undefined, 'normal');
          doc.text(thaiFullName, 20, yPosition);
          yPosition += 6;
        }
        
        doc.setFontSize(10);
        doc.text(`Employee ID: ${employee.employee_id}`, 20, yPosition);
        doc.text(`Status: ${employee.status}`, 120, yPosition);
        yPosition += 10;
        
        // Personal Information Section
        doc.setFillColor(52, 152, 219);
        doc.rect(20, yPosition - 3, 170, 8, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(11);
        doc.setFont(undefined, 'bold');
        doc.text('PERSONAL INFORMATION', 22, yPosition + 2);
        doc.setTextColor(0, 0, 0);
        yPosition += 8;
        
        doc.setFontSize(8);
        doc.setFont(undefined, 'normal');
        
        const personalInfo = [
          ['Title:', String(employee.title || 'N/A'), 'Gender:', String(employee.gender || 'N/A')],
          ['Date of Birth:', employee.date_of_birth ? new Date(employee.date_of_birth).toLocaleDateString('en-GB') : 'N/A', 'Nationality:', String(employee.nationality || 'N/A')],
          ['Religion:', String(employee.religion || 'N/A'), 'Place of Birth:', String(employee.place_of_birth || 'N/A')],
          ['English Nickname:', String(employee.english_nickname || 'N/A'), 'Thai Nickname:', String(employee.thai_nickname || 'N/A')]
        ];
        
        personalInfo.forEach(row => {
          doc.text(row[0], 20, yPosition);
          doc.text(row[1], 60, yPosition);
          doc.text(row[2], 120, yPosition);
          doc.text(row[3], 160, yPosition);
          yPosition += 4;
        });
        
        yPosition += 3;
        
        // Contact Information Section
        doc.setFillColor(39, 174, 96);
        doc.rect(20, yPosition - 3, 170, 8, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(11);
        doc.setFont(undefined, 'bold');
        doc.text('CONTACT INFORMATION', 22, yPosition + 2);
        doc.setTextColor(0, 0, 0);
        yPosition += 8;
        
        doc.setFontSize(8);
        doc.setFont(undefined, 'normal');
        
        const contactInfo = [
          ['Phone:', String(employee.phone_number || 'N/A'), 'Email:', String(employee.email || 'N/A')],
          ['Line ID:', String(employee.line_id || 'N/A'), '', ''],
          ['Emergency Contact:', String(employee.emergency_contact_name || 'N/A'), 'Emergency Phone:', String(employee.emergency_contact_phone || 'N/A')],
          ['Relationship:', String(employee.emergency_contact_relationship || 'N/A'), '', '']
        ];
        
        contactInfo.forEach(row => {
          if (row[0]) doc.text(row[0], 20, yPosition);
          if (row[1]) doc.text(row[1], 60, yPosition);
          if (row[2]) doc.text(row[2], 120, yPosition);
          if (row[3]) doc.text(row[3], 160, yPosition);
          yPosition += 4;
        });
        
        yPosition += 3;
        
        // Employment Information Section
        doc.setFillColor(155, 89, 182);
        doc.rect(20, yPosition - 3, 170, 8, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(11);
        doc.setFont(undefined, 'bold');
        doc.text('EMPLOYMENT INFORMATION', 22, yPosition + 2);
        doc.setTextColor(0, 0, 0);
        yPosition += 8;
        
        doc.setFontSize(8);
        doc.setFont(undefined, 'normal');
        
        const employmentInfo = [
          ['Position:', String(employee.position_name || (employee.position_id ? String(employee.position_id) : 'N/A')), 'Department:', String(employee.department_name || (employee.department_id ? String(employee.department_id) : 'N/A'))],
          ['Hire Date:', employee.hire_date ? new Date(employee.hire_date).toLocaleDateString('en-GB') : 'N/A', 'Employment Type:', String(employee.employment_type || 'N/A')],
          ['Salary:', employee.salary_amount ? `฿${parseFloat(employee.salary_amount).toLocaleString()}` : 'N/A', 'Salary Type:', String(employee.salary_type || 'N/A')],
          ['Probation End:', employee.probation_end_date ? new Date(employee.probation_end_date).toLocaleDateString('en-GB') : 'N/A', '', '']
        ];
        
        employmentInfo.forEach(row => {
          if (row[0]) doc.text(row[0], 20, yPosition);
          if (row[1]) doc.text(row[1], 60, yPosition);
          if (row[2]) doc.text(row[2], 120, yPosition);
          if (row[3]) doc.text(row[3], 160, yPosition);
          yPosition += 4;
        });
        
        yPosition += 3;
        
        // Address Information Section
        doc.setFillColor(231, 76, 60);
        doc.rect(20, yPosition - 3, 170, 8, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(11);
        doc.setFont(undefined, 'bold');
        doc.text('ADDRESS INFORMATION', 22, yPosition + 2);
        doc.setTextColor(0, 0, 0);
        yPosition += 8;
        
        doc.setFontSize(8);
        doc.setFont(undefined, 'normal');
        
        doc.text('Current Address:', 20, yPosition);
        yPosition += 4;
        doc.text(String(employee.current_address || employee.address || 'N/A'), 20, yPosition);
        yPosition += 4;
        
        const addressInfo = [
          ['Sub District:', String(employee.current_sub_district || 'N/A'), 'District:', String(employee.current_district || 'N/A')],
          ['Province:', String(employee.current_province || 'N/A'), 'Postal Code:', String(employee.current_postal_code || 'N/A')]
        ];
        
        addressInfo.forEach(row => {
          doc.text(row[0], 20, yPosition);
          doc.text(row[1], 60, yPosition);
          doc.text(row[2], 120, yPosition);
          doc.text(row[3], 160, yPosition);
          yPosition += 4;
        });
        
        yPosition += 2;
        doc.text('Permanent Address:', 20, yPosition);
        yPosition += 4;
        doc.text(String(employee.permanent_address || 'N/A'), 20, yPosition);
        yPosition += 4;
        doc.text(`Province: ${String(employee.permanent_province || 'N/A')}`, 20, yPosition);
        doc.text(`Postal Code: ${String(employee.permanent_postal_code || 'N/A')}`, 120, yPosition);
        yPosition += 6;
        
        // Identification Information Section
        doc.setFillColor(243, 156, 18);
        doc.rect(20, yPosition - 3, 170, 8, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(11);
        doc.setFont(undefined, 'bold');
        doc.text('IDENTIFICATION & BANKING', 22, yPosition + 2);
        doc.setTextColor(0, 0, 0);
        yPosition += 8;
        
        doc.setFontSize(8);
        doc.setFont(undefined, 'normal');
        
        const idInfo = [
          ['Thai ID:', String(employee.thai_id_number || employee.national_id || 'N/A'), 'Passport:', String(employee.passport_number || 'N/A')],
          ['Tax ID:', String(employee.tax_id || 'N/A'), 'Social Security:', String(employee.social_security_number || 'N/A')],
          ['Bank:', String(employee.bank_name || 'N/A'), 'Branch:', String(employee.bank_branch || 'N/A')],
          ['Account Number:', String(employee.bank_account_number || 'N/A'), 'Account Name:', String(employee.bank_account_name || 'N/A')]
        ];
        
        idInfo.forEach(row => {
          doc.text(row[0], 20, yPosition);
          doc.text(row[1], 60, yPosition);
          if (row[2]) doc.text(row[2], 120, yPosition);
          if (row[3]) doc.text(row[3], 160, yPosition);
          yPosition += 4;
        });
        
        yPosition += 3;
        
        // Visa & Immigration Section
        doc.setFillColor(142, 68, 173);
        doc.rect(20, yPosition - 3, 170, 8, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(11);
        doc.setFont(undefined, 'bold');
        doc.text('VISA & IMMIGRATION DETAILS', 22, yPosition + 2);
        doc.setTextColor(0, 0, 0);
        yPosition += 8;
        
        doc.setFontSize(8);
        doc.setFont(undefined, 'normal');
        
        const visaInfo = [
          ['Visa Type:', String(employee.visa_type || 'N/A'), 'Visa Expiry:', employee.visa_expiry ? new Date(employee.visa_expiry).toLocaleDateString('en-GB') : 'N/A'],
          ['Work Permit No.:', String(employee.work_permit_number || 'N/A'), 'Permit Expiry:', employee.work_permit_expiry ? new Date(employee.work_permit_expiry).toLocaleDateString('en-GB') : 'N/A'],
          ['Nationality:', String(employee.nationality || 'N/A'), 'Passport Number:', String(employee.passport_number || 'N/A')]
        ];
        
        visaInfo.forEach(row => {
          doc.text(row[0], 20, yPosition);
          doc.text(row[1], 60, yPosition);
          if (row[2]) doc.text(row[2], 120, yPosition);
          if (row[3]) doc.text(row[3], 160, yPosition);
          yPosition += 4;
        });
        
        // Add visa status information
        yPosition += 2;
        doc.setFont(undefined, 'bold');
        doc.text('Status:', 20, yPosition);
        doc.setFont(undefined, 'normal');
        
        // Check visa expiry status
        if (employee.visa_expiry) {
          const visaExpiry = new Date(employee.visa_expiry);
          const today = new Date();
          const daysUntilExpiry = Math.ceil((visaExpiry - today) / (1000 * 60 * 60 * 24));
          
          if (daysUntilExpiry < 0) {
            doc.text('Visa EXPIRED', 60, yPosition);
          } else if (daysUntilExpiry <= 30) {
            doc.text(`Visa expires in ${daysUntilExpiry} days`, 60, yPosition);
          } else {
            doc.text('Visa Valid', 60, yPosition);
          }
        } else {
          doc.text('No visa information', 60, yPosition);
        }
        
        // Check work permit expiry status
        if (employee.work_permit_expiry) {
          const permitExpiry = new Date(employee.work_permit_expiry);
          const today = new Date();
          const daysUntilExpiry = Math.ceil((permitExpiry - today) / (1000 * 60 * 60 * 24));
          
          if (daysUntilExpiry < 0) {
            doc.text('Work Permit EXPIRED', 120, yPosition);
          } else if (daysUntilExpiry <= 30) {
            doc.text(`Work Permit expires in ${daysUntilExpiry} days`, 120, yPosition);
          } else {
            doc.text('Work Permit Valid', 120, yPosition);
          }
        } else {
          doc.text('No work permit info', 120, yPosition);
        }
        
        yPosition += 5;
        
        // Notes section with highlighted heading (if notes exist)
        if (employee.notes) {
          yPosition += 5;
          doc.setFillColor(231, 76, 60); // Red background
          doc.rect(20, yPosition - 3, 170, 8, 'F');
          doc.setTextColor(255, 255, 255);
          doc.setFontSize(11);
          doc.setFont(undefined, 'bold');
          doc.text('ADDITIONAL NOTES', 22, yPosition + 2);
          doc.setTextColor(0, 0, 0);
          yPosition += 8;
          
          doc.setFontSize(8);
          doc.setFont(undefined, 'normal');
          
          // Split long text into multiple lines
          const splitText = doc.splitTextToSize(String(employee.notes), 170);
          splitText.forEach(line => {
            if (yPosition > 280) { // Add new page if needed
              doc.addPage();
              yPosition = 30; // Leave space for potential header on new page
            }
            doc.text(line, 20, yPosition);
            yPosition += 4;
          });
        }
      }

      // Add footer to all pages
      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.text(`Page ${i} of ${pageCount}`, 190, 285, { align: 'right' });
        doc.text(`Generated by Jannaat HR System`, 20, 285);
      }

      // Generate filename and save
      const fileName = `jannaat_selected_employees_profiles_${new Date().toISOString().split('T')[0]}.pdf`;
      doc.save(fileName);
      
      toast.success(`PDF exported successfully! ${selectedEmployees.length} employee profiles included.`);
      
      // Clear selections after successful export
      setSelectedEmployees([]);
      setSelectAll(false);
      
    } catch (error) {
      console.error('Error exporting selected employees to PDF:', error);
      toast.error('Failed to export selected employees to PDF');
    }
  };

  // Export single employee to PDF with logo and photo
  const exportEmployeeToPDF = async (employee) => {
    try {
      const doc = new jsPDF('p', 'mm', 'a4'); // Portrait A4
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      
      let yPosition = 10;
      
      // Add logo to header (top left)
      try {
        doc.addImage(logo, 'PNG', 10, yPosition, 30, 15);
      } catch (error) {
        console.log('Logo could not be added to PDF');
      }
      
      // Add company header
      doc.setFontSize(18);
      doc.setFont(undefined, 'bold');
      doc.setTextColor(41, 128, 185);
      doc.text('JANNAAT HR MANAGEMENT SYSTEM', pageWidth / 2, yPosition + 8, { align: 'center' });
      
      doc.setFontSize(12);
      doc.setFont(undefined, 'normal');
      doc.setTextColor(100, 100, 100);
      doc.text('Employee Information Report', pageWidth / 2, yPosition + 15, { align: 'center' });
      
      doc.setFontSize(9);
      const currentDate = new Date();
      doc.text(`Generated on: ${currentDate.toLocaleDateString('en-GB')} at ${currentDate.toLocaleTimeString('en-GB')}`, pageWidth / 2, yPosition + 20, { align: 'center' });
      
      // Add separator line
      doc.setDrawColor(41, 128, 185);
      doc.setLineWidth(0.5);
      doc.line(10, yPosition + 23, pageWidth - 10, yPosition + 23);
      
      yPosition = 40;
      
      // Try to add employee profile photo
      if (employee.profile_photo_url) {
        try {
          // Convert image to base64
          const response = await fetch(employee.profile_photo_url);
          const blob = await response.blob();
          const reader = new FileReader();
          
          await new Promise((resolve, reject) => {
            reader.onloadend = () => {
              try {
                doc.addImage(reader.result, 'JPEG', pageWidth - 40, yPosition, 25, 30);
                resolve();
              } catch (error) {
                console.log('Could not add employee photo to PDF');
                resolve();
              }
            };
            reader.onerror = reject;
            reader.readAsDataURL(blob);
          });
        } catch (error) {
          console.log('Failed to load employee photo for PDF');
        }
      }
      
      // Employee Basic Information
      doc.setFontSize(14);
      doc.setFont(undefined, 'bold');
      doc.setTextColor(0, 0, 0);
      const englishName = `${employee.english_first_name || ''} ${employee.english_last_name || ''}`.trim();
      const thaiName = `${employee.thai_first_name || ''} ${employee.thai_last_name || ''}`.trim();
      
      doc.text(englishName, 10, yPosition);
      yPosition += 6;
      
      doc.setFontSize(11);
      doc.setFont(undefined, 'normal');
      doc.setTextColor(100, 100, 100);
      if (thaiName) {
        doc.text(thaiName, 10, yPosition);
        yPosition += 6;
      }
      
      doc.setFontSize(9);
      doc.setTextColor(0, 0, 0);
      doc.text(`Employee ID: ${employee.employee_id}`, 10, yPosition);
      doc.text(`Status: ${employee.status}`, 10, yPosition + 5);
      yPosition += 12;
      
      // Section helper function
      const addSection = (title, color, data) => {
        if (yPosition > pageHeight - 30) {
          doc.addPage();
          yPosition = 20;
        }
        
        doc.setFillColor(...color);
        doc.rect(10, yPosition - 3, pageWidth - 20, 8, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(11);
        doc.setFont(undefined, 'bold');
        doc.text(title, 12, yPosition + 2);
        doc.setTextColor(0, 0, 0);
        yPosition += 10;
        
        doc.setFontSize(8);
        doc.setFont(undefined, 'normal');
        
        data.forEach(row => {
          if (yPosition > pageHeight - 15) {
            doc.addPage();
            yPosition = 20;
          }
          
          if (row.length === 2) {
            doc.setFont(undefined, 'bold');
            doc.text(row[0], 12, yPosition);
            doc.setFont(undefined, 'normal');
            doc.text(String(row[1] || 'N/A'), 70, yPosition);
          } else if (row.length === 4) {
            doc.setFont(undefined, 'bold');
            doc.text(row[0], 12, yPosition);
            doc.setFont(undefined, 'normal');
            doc.text(String(row[1] || 'N/A'), 55, yPosition);
            doc.setFont(undefined, 'bold');
            doc.text(row[2], 115, yPosition);
            doc.setFont(undefined, 'normal');
            doc.text(String(row[3] || 'N/A'), 155, yPosition);
          }
          yPosition += 5;
        });
        
        yPosition += 5;
      };
      
      // Personal Information
      addSection('PERSONAL INFORMATION', [41, 128, 185], [
        ['Title:', employee.title, 'Gender:', employee.gender],
        ['Date of Birth:', employee.date_of_birth ? new Date(employee.date_of_birth).toLocaleDateString('en-GB') : 'N/A', 'Nationality:', employee.nationality],
        ['Religion:', employee.religion, 'Place of Birth:', employee.place_of_birth],
        ['English Nickname:', employee.english_nickname, 'Thai Nickname:', employee.thai_nickname]
      ]);
      
      // Contact Information
      addSection('CONTACT INFORMATION', [46, 204, 113], [
        ['Phone:', employee.phone_number, 'Email:', employee.email],
        ['Line ID:', employee.line_id, '', ''],
        ['Emergency Contact:', employee.emergency_contact_name, 'Emergency Phone:', employee.emergency_contact_phone],
        ['Relationship:', employee.emergency_contact_relationship, '', '']
      ]);
      
      // Employment Information
      addSection('EMPLOYMENT INFORMATION', [230, 126, 34], [
        ['Position:', employee.position_name || employee.position_id, 'Department:', employee.department_name || employee.department_id],
        ['Hire Date:', employee.hire_date ? new Date(employee.hire_date).toLocaleDateString('en-GB') : 'N/A', 'Employment Type:', employee.employment_type],
        ['Salary:', employee.salary_amount ? `THB ${parseFloat(employee.salary_amount).toLocaleString()}` : 'N/A', 'Salary Type:', employee.salary_type],
        ['Probation End:', employee.probation_end_date ? new Date(employee.probation_end_date).toLocaleDateString('en-GB') : 'N/A', '', '']
      ]);
      
      // Address Information
      doc.setFillColor(155, 89, 182);
      doc.rect(10, yPosition - 3, pageWidth - 20, 8, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(11);
      doc.setFont(undefined, 'bold');
      doc.text('ADDRESS INFORMATION', 12, yPosition + 2);
      doc.setTextColor(0, 0, 0);
      yPosition += 10;
      
      doc.setFontSize(8);
      doc.setFont(undefined, 'bold');
      doc.text('Current Address:', 12, yPosition);
      yPosition += 4;
      doc.setFont(undefined, 'normal');
      const currentAddress = doc.splitTextToSize(employee.current_address || 'N/A', pageWidth - 24);
      currentAddress.forEach(line => {
        doc.text(line, 12, yPosition);
        yPosition += 4;
      });
      
      doc.setFont(undefined, 'bold');
      doc.text('Sub District:', 12, yPosition);
      doc.setFont(undefined, 'normal');
      doc.text(employee.current_sub_district || 'N/A', 55, yPosition);
      doc.setFont(undefined, 'bold');
      doc.text('District:', 115, yPosition);
      doc.setFont(undefined, 'normal');
      doc.text(employee.current_district || 'N/A', 155, yPosition);
      yPosition += 5;
      
      doc.setFont(undefined, 'bold');
      doc.text('Province:', 12, yPosition);
      doc.setFont(undefined, 'normal');
      doc.text(employee.current_province || 'N/A', 55, yPosition);
      doc.setFont(undefined, 'bold');
      doc.text('Postal Code:', 115, yPosition);
      doc.setFont(undefined, 'normal');
      doc.text(employee.current_postal_code || 'N/A', 155, yPosition);
      yPosition += 7;
      
      doc.setFont(undefined, 'bold');
      doc.text('Permanent Address:', 12, yPosition);
      yPosition += 4;
      doc.setFont(undefined, 'normal');
      const permanentAddress = doc.splitTextToSize(employee.permanent_address || 'N/A', pageWidth - 24);
      permanentAddress.forEach(line => {
        doc.text(line, 12, yPosition);
        yPosition += 4;
      });
      
      doc.setFont(undefined, 'bold');
      doc.text('Province:', 12, yPosition);
      doc.setFont(undefined, 'normal');
      doc.text(employee.permanent_province || 'N/A', 55, yPosition);
      doc.setFont(undefined, 'bold');
      doc.text('Postal Code:', 115, yPosition);
      doc.setFont(undefined, 'normal');
      doc.text(employee.permanent_postal_code || 'N/A', 155, yPosition);
      yPosition += 8;
      
      // Identification & Banking
      addSection('IDENTIFICATION & BANKING', [52, 152, 219], [
        ['Thai ID:', employee.thai_id_number, 'Passport:', employee.passport_number],
        ['Tax ID:', employee.tax_id, 'Social Security:', employee.social_security_number],
        ['Bank:', employee.bank_name, 'Branch:', employee.bank_branch],
        ['Account Number:', employee.bank_account_number, 'Account Name:', employee.bank_account_name]
      ]);
      
      // Visa & Immigration
      addSection('VISA & IMMIGRATION DETAILS', [230, 126, 34], [
        ['Visa Type:', employee.visa_type, 'Visa Expiry:', employee.visa_expiry ? new Date(employee.visa_expiry).toLocaleDateString('en-GB') : 'N/A'],
        ['Work Permit No.:', employee.work_permit_number, 'Permit Expiry:', employee.work_permit_expiry ? new Date(employee.work_permit_expiry).toLocaleDateString('en-GB') : 'N/A']
      ]);
      
      // Notes
      if (employee.notes) {
        if (yPosition > pageHeight - 30) {
          doc.addPage();
          yPosition = 20;
        }
        
        doc.setFillColor(231, 76, 60);
        doc.rect(10, yPosition - 3, pageWidth - 20, 8, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(11);
        doc.setFont(undefined, 'bold');
        doc.text('ADDITIONAL NOTES', 12, yPosition + 2);
        doc.setTextColor(0, 0, 0);
        yPosition += 10;
        
        doc.setFontSize(8);
        doc.setFont(undefined, 'normal');
        const splitNotes = doc.splitTextToSize(employee.notes, pageWidth - 24);
        splitNotes.forEach(line => {
          if (yPosition > pageHeight - 15) {
            doc.addPage();
            yPosition = 20;
          }
          doc.text(line, 12, yPosition);
          yPosition += 4;
        });
      }
      
      // Add footer to all pages
      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(150, 150, 150);
        doc.text(`Page ${i} of ${pageCount}`, pageWidth - 30, pageHeight - 10);
        doc.text('Generated by Jannaat HR System', 10, pageHeight - 10);
      }
      
      // Save the PDF
      const fileName = `Employee_${employee.employee_id}_${employee.english_first_name || 'Unknown'}_${new Date().toISOString().split('T')[0]}.pdf`;
      doc.save(fileName);
      
      toast.success('Employee PDF exported successfully!');
    } catch (error) {
      console.error('Error generating employee PDF:', error);
      toast.error('Failed to generate employee PDF');
    }
  };

  // Sample CSV file download
  const downloadSampleCSV = () => {
    const sampleData = [
      [
        'employee_id', 'thai_first_name', 'thai_last_name', 'english_first_name', 'english_last_name',
        'thai_nickname', 'english_nickname', 'thai_id_number', 'passport_number', 'nationality', 'religion',
        'date_of_birth', 'place_of_birth', 'phone_number', 'email', 'line_id', 
        'emergency_contact_name', 'emergency_contact_phone', 'emergency_contact_relationship',
        'current_address', 'current_sub_district', 'current_district', 'current_province', 'current_postal_code',
        'permanent_address', 'permanent_sub_district', 'permanent_district', 'permanent_province', 'permanent_postal_code',
        'department_id', 'position_id', 'hire_date', 'employment_type', 'work_permit_number', 'work_permit_expiry',
        'visa_type', 'visa_expiry', 'salary_amount', 'salary_type', 'social_security_number', 'tax_id',
        'bank_name', 'bank_branch', 'bank_account_number', 'bank_account_name',
        'status', 'probation_end_date', 'notes'
      ],
      [
        'EMP001', 'จอห์น', 'โด', 'John', 'Doe', 'จอนนี่', 'Johnny', '1234567890123', 'A12345678',
        'American', 'Christian', '1990-01-15', 'New York', '0812345678', 'john.doe@example.com', 'johndoe123',
        'Jane Doe', '0898765432', 'Wife', '123 Main Street', 'Sathorn', 'Bangrak', 'Bangkok', '10500',
        '456 Oak Avenue', 'Sathorn', 'Bangrak', 'Bangkok', '10500', '3', '22',
        '2023-01-01', 'Full-time', 'WP123456', '2025-12-31', 'Non-B', '2025-12-31',
        '50000', 'Monthly', 'SS123456789', '1234567890', 'Bangkok Bank', 'Silom Branch',
        '1234567890', 'John Doe', 'Active', '2023-07-01', 'Experienced software developer'
      ]
    ];

    const csvContent = sampleData.map(row => 
      row.map(cell => `"${cell}"`).join(',')
    ).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'employee_sample_format.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast.success('Sample CSV file downloaded successfully!');
  };

  // Handle bulk CSV upload
  const handleBulkUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.name.endsWith('.csv')) {
      toast.error('Please select a CSV file');
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);
    setUploadResults(null);

    try {
      // Create FormData for file upload
      const formData = new FormData();
      formData.append('csvFile', file);

      const { headers } = getHeaders();

      // Show initial progress
      setUploadProgress(10);

      // Upload file to backend API
      const response = await axios.post(
        'upload-employee-csv', 
        formData, 
        { 
          headers,
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round((progressEvent.loaded * 80) / progressEvent.total) + 10;
            setUploadProgress(percentCompleted);
          }
        }
      );

      // Complete progress
      setUploadProgress(100);

      const apiResults = response.data;

      if (apiResults.success) {
        const results = {
          total: apiResults.summary.totalRows,
          successful: apiResults.summary.successfulInserts,
          failed: apiResults.summary.failedInserts,
          errors: apiResults.errors || []
        };

        setUploadResults(results);
        
        if (results.successful > 0) {
          toast.success(`Successfully uploaded ${results.successful} employees!`);
          // Refresh the employee list
          setRefreshTrigger(prev => prev + 1);
        }
        
        if (results.failed > 0) {
          toast.warn(`${results.failed} employees failed to upload. Check the results for details.`);
        }

        if (results.total === 0) {
          toast.info('No valid employee data found in CSV file');
        }

      } else {
        toast.error(apiResults.message || 'Failed to process CSV file');
        setUploadResults({
          total: 0,
          successful: 0,
          failed: 1,
          errors: [{ row: 1, employee_id: 'N/A', error: apiResults.message || 'Unknown error' }]
        });
      }

    } catch (error) {
      console.error('Error uploading CSV file:', error);
      
      let errorMessage = 'Failed to upload CSV file';
      
      if (error.response) {
        // Server responded with error status
        errorMessage = error.response.data?.message || `Server error: ${error.response.status}`;
      } else if (error.request) {
        // Network error
        errorMessage = 'Cannot connect to server. Please check your internet connection.';
      } else {
        // Other error
        errorMessage = error.message || 'An unexpected error occurred';
      }
      
      toast.error(errorMessage);
      
      setUploadResults({
        total: 0,
        successful: 0,
        failed: 1,
        errors: [{ row: 1, employee_id: 'N/A', error: errorMessage }]
      });
    } finally {
      setIsUploading(false);
      // Reset file input
      event.target.value = '';
    }
  };

  // Function to convert image to base64
  const getImageAsBase64 = async (url) => {
    try {
      // First try with fetch (better for CORS)
      const response = await fetch(url);
      if (response.ok) {
        const blob = await response.blob();
        return new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });
      }
    } catch (fetchError) {
      console.log('Fetch method failed, trying Image element:', fetchError);
    }
    
    // Fallback to Image element method
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        canvas.width = img.width;
        canvas.height = img.height;
        ctx.drawImage(img, 0, 0);
        try {
          const dataURL = canvas.toDataURL('image/jpeg', 0.8);
          resolve(dataURL);
        } catch (error) {
          reject(error);
        }
      };
      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = url;
    });
  };

  // Function to get dummy profile image as base64
  const getDummyProfileImage = () => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    canvas.width = 100;
    canvas.height = 100;
    
    // Draw a simple person icon
    ctx.fillStyle = '#e0e0e0';
    ctx.fillRect(0, 0, 100, 100);
    
    // Head circle
    ctx.fillStyle = '#757575';
    ctx.beginPath();
    ctx.arc(50, 30, 15, 0, 2 * Math.PI);
    ctx.fill();
    
    // Body
    ctx.beginPath();
    ctx.arc(50, 80, 25, Math.PI, 0, false);
    ctx.fill();
    
    return canvas.toDataURL('image/jpeg', 0.8);
  };

  return (
    <>
      <Layout>
        <Header title="Employee Management" />
        <ToastContainer />
        
        {!showAddForm ? (
          <div className="row">
            <div className="col-lg-12 col-md-12 col-sm-12 col-xs-12">
              <CardComponent
                title="Employee Management System"
                headerColor="primary"
                pull="left"
                bodyClass="panel-body"
                headerContent={
                  <div className="d-flex align-items-center">
                    <i className="zmdi zmdi-store me-2"></i>
                    <span>Quick Actions & Management</span>
                  </div>
                }
              >
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <div>
                    <h5>Employee Records</h5>
                    <p className="text-muted">Manage employee information, contracts, and details</p>
                  </div>
                  <div className="d-flex gap-2">
                    <MaterialButton 
                      variant="danger" 
                      onClick={exportToPDF}
                      icon="zmdi zmdi-file-text"
                      rounded={true}
                      title="Export to PDF"
                    >
                      Export PDF
                    </MaterialButton>
                    <MaterialButton 
                      variant="info" 
                      onClick={exportSelectedEmployeesToPDF}
                      icon="zmdi zmdi-accounts"
                      rounded={true}
                      title={`Export Selected (${selectedEmployees.length}) Employee Profiles to PDF`}
                      disabled={selectedEmployees.length === 0}
                    >
                      Export Selected ({selectedEmployees.length})
                    </MaterialButton>
                    <MaterialButton 
                      variant="warning" 
                      onClick={() => setShowBulkUpload(true)}
                      icon="zmdi zmdi-upload"
                      rounded={true}
                      title="Bulk Upload CSV"
                    >
                      Bulk Upload CSV
                    </MaterialButton>
                    <MaterialButton 
                      variant="primary" 
                      onClick={() => {
                        setEditingEmployee(null);
                        setShowAddForm(true);
                      }}
                      icon="zmdi zmdi-plus"
                      rounded={true}
                    >
                      Add New Employee
                    </MaterialButton>
                  </div>
                </div>
                
                <div className="row">
                  <div className="col-xl-3 col-lg-3 col-md-6 col-sm-6 col-xs-12 mb-3">
                    <div className="card h-100 shadow-lg border-0" style={{
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      borderRadius: '15px',
                      transform: 'translateY(0)',
                      transition: 'all 0.3s ease',
                      cursor: 'pointer'
                    }}
                    onClick={() => handleStatusFilter('All')}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-5px)';
                      e.currentTarget.style.boxShadow = '0 15px 35px rgba(102, 126, 234, 0.4)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 10px 25px rgba(0,0,0,0.15)';
                    }}>
                      <div className="card-body text-center text-white p-3">
                        <div className="d-flex align-items-center justify-content-center mb-2">
                          <div className="rounded-circle p-2" style={{
                            backgroundColor: 'rgba(255,255,255,0.2)',
                            backdropFilter: 'blur(10px)'
                          }}>
                            <i className="zmdi zmdi-accounts" style={{ fontSize: '1.8rem' }}></i>
                          </div>
                        </div>
                        <h2 className="fw-bold mb-1" style={{ fontSize: '2.2rem' }}>
                          {loading ? '...' : statistics.total}
                        </h2>
                        <p className="mb-0 opacity-90" style={{ fontSize: '1rem' }}>Total Employees</p>
                        <div className="mt-1">
                          <small className="opacity-75">Click to view all</small>
                        </div>
                        <div className="mt-2">
                          <div className="progress" style={{ height: '4px', backgroundColor: 'rgba(255,255,255,0.3)' }}>
                            <div className="progress-bar bg-white" style={{ width: '100%' }}></div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="col-xl-3 col-lg-3 col-md-6 col-sm-6 col-xs-12 mb-3">
                    <div className="card h-100 shadow-lg border-0" style={{
                      background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                      borderRadius: '15px',
                      transform: 'translateY(0)',
                      transition: 'all 0.3s ease',
                      cursor: 'pointer'
                    }}
                    onClick={() => handleStatusFilter('Active')}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-5px)';
                      e.currentTarget.style.boxShadow = '0 15px 35px rgba(17, 153, 142, 0.4)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 10px 25px rgba(0,0,0,0.15)';
                    }}>
                      <div className="card-body text-center text-white p-3">
                        <div className="d-flex align-items-center justify-content-center mb-2">
                          <div className="rounded-circle p-2" style={{
                            backgroundColor: 'rgba(255,255,255,0.2)',
                            backdropFilter: 'blur(10px)'
                          }}>
                            <i className="zmdi zmdi-check-circle" style={{ fontSize: '1.8rem' }}></i>
                          </div>
                        </div>
                        <h2 className="fw-bold mb-1" style={{ fontSize: '2.2rem' }}>
                          {loading ? '...' : statistics.active}
                        </h2>
                        <p className="mb-0 opacity-90" style={{ fontSize: '1rem' }}>Active Employees</p>
                        <div className="mt-1">
                          <small className="opacity-75">Click to filter active</small>
                        </div>
                        <div className="mt-2">
                          <div className="progress" style={{ height: '4px', backgroundColor: 'rgba(255,255,255,0.3)' }}>
                            <div className="progress-bar bg-white" 
                                 style={{ width: `${statistics.total > 0 ? (statistics.active/statistics.total)*100 : 0}%` }}>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="col-xl-3 col-lg-3 col-md-6 col-sm-6 col-xs-12 mb-3">
                    <div className="card h-100 shadow-lg border-0" style={{
                      background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                      borderRadius: '15px',
                      transform: 'translateY(0)',
                      transition: 'all 0.3s ease',
                      cursor: 'pointer'
                    }}
                    onClick={() => handleStatusFilter('Probation')}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-5px)';
                      e.currentTarget.style.boxShadow = '0 15px 35px rgba(240, 147, 251, 0.4)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 10px 25px rgba(0,0,0,0.15)';
                    }}>
                      <div className="card-body text-center text-white p-3">
                        <div className="d-flex align-items-center justify-content-center mb-2">
                          <div className="rounded-circle p-2" style={{
                            backgroundColor: 'rgba(255,255,255,0.2)',
                            backdropFilter: 'blur(10px)'
                          }}>
                            <i className="zmdi zmdi-time" style={{ fontSize: '1.8rem' }}></i>
                          </div>
                        </div>
                        <h2 className="fw-bold mb-1" style={{ fontSize: '2.2rem' }}>
                          {loading ? '...' : statistics.probation}
                        </h2>
                        <p className="mb-0 opacity-90" style={{ fontSize: '1rem' }}>On Probation</p>
                        <div className="mt-1">
                          <small className="opacity-75">Click to filter probation</small>
                        </div>
                        <div className="mt-2">
                          <div className="progress" style={{ height: '4px', backgroundColor: 'rgba(255,255,255,0.3)' }}>
                            <div className="progress-bar bg-white" 
                                 style={{ width: `${statistics.total > 0 ? (statistics.probation/statistics.total)*100 : 0}%` }}>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="col-xl-3 col-lg-3 col-md-6 col-sm-6 col-xs-12 mb-3">
                    <div className="card h-100 shadow-lg border-0" style={{
                      background: 'linear-gradient(135deg, #a8a8a8 0%, #6c757d 100%)',
                      borderRadius: '15px',
                      transform: 'translateY(0)',
                      transition: 'all 0.3s ease',
                      cursor: 'pointer'
                    }}
                    onClick={() => handleStatusFilter('Inactive')}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-5px)';
                      e.currentTarget.style.boxShadow = '0 15px 35px rgba(168, 168, 168, 0.4)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 10px 25px rgba(0,0,0,0.15)';
                    }}>
                      <div className="card-body text-center text-white p-3">
                        <div className="d-flex align-items-center justify-content-center mb-2">
                          <div className="rounded-circle p-2" style={{
                            backgroundColor: 'rgba(255,255,255,0.2)',
                            backdropFilter: 'blur(10px)'
                          }}>
                            <i className="zmdi zmdi-pause" style={{ fontSize: '1.8rem' }}></i>
                          </div>
                        </div>
                        <h2 className="fw-bold mb-1" style={{ fontSize: '2.2rem' }}>
                          {loading ? '...' : statistics.inactive}
                        </h2>
                        <p className="mb-0 opacity-90" style={{ fontSize: '1rem' }}>Inactive</p>
                        <div className="mt-1">
                          <small className="opacity-75">Click to filter inactive</small>
                        </div>
                        <div className="mt-2">
                          <div className="progress" style={{ height: '4px', backgroundColor: 'rgba(255,255,255,0.3)' }}>
                            <div className="progress-bar bg-white" 
                                 style={{ width: `${statistics.total > 0 ? (statistics.inactive/statistics.total)*100 : 0}%` }}>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Show appropriate content based on data state */}
                {loading ? (
                  <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-3">Loading employees...</p>
                  </div>
                ) : employees.length === 0 ? (
                  <div className="text-center py-5">
                    <div className="mb-4">
                      <i className="zmdi zmdi-account-box-o" style={{ fontSize: '4rem', color: '#ccc' }}></i>
                    </div>
                    <h5 className="text-muted">No employees found</h5>
                    <p className="text-muted">
                      {statistics.total === 0 ? 
                        'No employee records are available in the database.' : 
                        'Unable to load employee data from the server.'
                      }
                    </p>
                    <MaterialButton 
                      variant="primary" 
                      onClick={() => {
                        setEditingEmployee(null);
                        setShowAddForm(true);
                      }}
                      icon="zmdi zmdi-plus"
                      rounded={true}
                    >
                      Add First Employee
                    </MaterialButton>
                  </div>
                ) : (
                  <>
                    {/* Filter Status Indicator */}
                    {statusFilter !== 'All' && (
                      <div className="alert alert-info d-flex justify-content-between align-items-center mb-3">
                        <div>
                          <i className="zmdi zmdi-filter-list me-2"></i>
                          <strong>Filtered by:</strong> {statusFilter} employees ({filteredEmployees.length} records)
                        </div>
                        <button 
                          className="btn btn-sm btn-outline-primary"
                          onClick={() => handleStatusFilter('All')}
                        >
                          <i className="zmdi zmdi-close me-1"></i>
                          Clear Filter
                        </button>
                      </div>
                    )}
                    
                    {/* Selection Summary */}
                    {selectedEmployees.length > 0 && (
                      <div className="alert alert-success d-flex justify-content-between align-items-center mb-3">
                        <div>
                          <i className="zmdi zmdi-info-outline me-2"></i>
                          <strong>{selectedEmployees.length}</strong> employee{selectedEmployees.length !== 1 ? 's' : ''} selected for export
                        </div>
                        <MaterialButton 
                          variant="secondary" 
                          size="small"
                          onClick={() => {
                            setSelectedEmployees([]);
                            setSelectAll(false);
                          }}
                          title="Clear Selection"
                        >
                          Clear Selection
                        </MaterialButton>
                      </div>
                    )}
                    
                    <EmployeeDataTable 
                      onEdit={handleEditEmployee}
                      onView={handleViewEmployeeDetails}
                      onDelete={() => setRefreshTrigger(prev => prev + 1)}
                      refreshTrigger={refreshTrigger}
                      showActions={true}
                      showSearch={true}
                      showPagination={true}
                      filteredData={statusFilter !== 'All' ? filteredEmployees : null}
                      filterStatus={statusFilter}
                      // Multi-selection props
                      selectedEmployees={selectedEmployees}
                      onSelectEmployee={handleSelectEmployee}
                      onSelectAll={handleSelectAll}
                      selectAll={selectAll}
                      showSelection={true}
                    />
                  </>
                )}
              </CardComponent>
            </div>
          </div>
        ) : (
          <AddNewEmployeeForm
            onClose={() => {
              setShowAddForm(false);
              setEditingEmployee(null);
              setDuplicateFields([]);
              setAutoGeneratedFields({});
            }}
            onSave={handleSaveEmployee}
            editingEmployee={editingEmployee}
            isEditMode={!!editingEmployee}
            duplicateFields={duplicateFields}
            autoGeneratedFields={autoGeneratedFields}
          />
        )}
      </Layout>

      {/* Employee Detail Modal - Ant Design */}
      <Modal
        title={
          <Space>
            <UserIcon />
            <span>Employee Information</span>
          </Space>
        }
        open={showDetailModal}
        onCancel={() => setShowDetailModal(false)}
        width="90%"
        style={{ maxWidth: '1200px', top: 20 }}
        footer={[
          <Button key="close" onClick={() => setShowDetailModal(false)}>
            Close
          </Button>,
          <Button 
            key="pdf" 
            type="default" 
            icon={<PrinterOutlined />}
            onClick={() => selectedEmployee && exportEmployeeToPDF(selectedEmployee)}
          >
            Export PDF
          </Button>,
          <Button 
            key="edit" 
            type="primary" 
            icon={<EditOutlined />}
            onClick={() => {
              setShowDetailModal(false);
              selectedEmployee && handleEditEmployee(selectedEmployee);
            }}
          >
            Edit Employee
          </Button>
        ]}
      >
        {selectedEmployee && (
          <div id="employeeDetailsToPrint" style={{ maxHeight: 'calc(90vh - 200px)', overflowY: 'auto' }}>
                  
            {/* Employee Header Section */}
            <Card style={{ marginBottom: 24 }}>
              <Row gutter={24}>
                <Col xs={24} md={6} style={{ textAlign: 'center' }}>
                  {selectedEmployee.profile_photo_url || selectedEmployee.profile_photo ? (
                    <img
                      src={selectedEmployee.profile_photo_url || `http://localhost:4402/uploads/${selectedEmployee.profile_photo}`}
                      alt="Employee Profile"
                      style={{ 
                        width: 120, 
                        height: 140, 
                        objectFit: 'cover', 
                        borderRadius: 8,
                        border: '1px solid #d9d9d9'
                      }}
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                  ) : null}
                  <div style={{ 
                    display: (selectedEmployee.profile_photo_url || selectedEmployee.profile_photo) ? 'none' : 'flex',
                    width: 120,
                    height: 140,
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto'
                  }}>
                    <Avatar size={120} icon={<UserIcon />} />
                  </div>
                </Col>
                <Col xs={24} md={18}>
                  <div style={{ marginBottom: 16 }}>
                    <h2 style={{ marginBottom: 4, color: '#1890ff' }}>
                      {`${selectedEmployee.english_first_name || ''} ${selectedEmployee.english_last_name || ''}`.trim()}
                    </h2>
                    <h4 style={{ color: '#888', marginBottom: 8 }}>
                      {`${selectedEmployee.thai_first_name || ''} ${selectedEmployee.thai_last_name || ''}`.trim()}
                    </h4>
                  </div>
                  <Row gutter={16}>
                    <Col span={8}>
                      <div>
                        <strong style={{ color: '#1890ff' }}>Employee ID:</strong>
                        <div style={{ fontSize: 16, fontWeight: 'bold' }}>{selectedEmployee.employee_id}</div>
                      </div>
                    </Col>
                    <Col span={8}>
                      <div>
                        <strong style={{ color: '#1890ff' }}>Department:</strong>
                        <div>{selectedEmployee.department_name || selectedEmployee.department_id || 'N/A'}</div>
                      </div>
                    </Col>
                    <Col span={8}>
                      <div>
                        <strong style={{ color: '#1890ff' }}>Hire Date:</strong>
                        <div>{selectedEmployee.hire_date ? new Date(selectedEmployee.hire_date).toLocaleDateString('en-GB') : 'N/A'}</div>
                      </div>
                    </Col>
                  </Row>
                  <Row gutter={16} style={{ marginTop: 16 }}>
                    <Col span={8}>
                      <div>
                        <strong style={{ color: '#1890ff' }}>Position:</strong>
                        <div>{selectedEmployee.position_name || selectedEmployee.position_id || 'N/A'}</div>
                      </div>
                    </Col>
                    <Col span={8}>
                      <div>
                        <strong style={{ color: '#1890ff' }}>Status:</strong>
                        <div>
                          <Tag color={
                            selectedEmployee.status === 'Active' ? 'success' :
                            selectedEmployee.status === 'Probation' ? 'warning' :
                            selectedEmployee.status === 'Inactive' ? 'default' : 'error'
                          }>
                            {selectedEmployee.status}
                          </Tag>
                        </div>
                      </div>
                    </Col>
                  </Row>
                </Col>
              </Row>
            </Card>

            {/* Personal Information Section */}
            <Card 
              title={<Space><UserIcon /> Personal Information</Space>}
              style={{ marginBottom: 24 }}
              headStyle={{ backgroundColor: '#1890ff', color: 'white' }}
            >
              <Descriptions bordered column={{ xs: 1, sm: 2, md: 2 }}>
                <Descriptions.Item label="Title">{selectedEmployee.title || 'N/A'}</Descriptions.Item>
                <Descriptions.Item label="Gender">{selectedEmployee.gender || 'N/A'}</Descriptions.Item>
                <Descriptions.Item label="Date of Birth">
                  {selectedEmployee.date_of_birth ? new Date(selectedEmployee.date_of_birth).toLocaleDateString('en-GB') : 'N/A'}
                </Descriptions.Item>
                <Descriptions.Item label="Nationality">{selectedEmployee.nationality || 'N/A'}</Descriptions.Item>
                <Descriptions.Item label="Religion">{selectedEmployee.religion || 'N/A'}</Descriptions.Item>
                <Descriptions.Item label="Place of Birth">{selectedEmployee.place_of_birth || 'N/A'}</Descriptions.Item>
                <Descriptions.Item label="English Nickname">{selectedEmployee.english_nickname || 'N/A'}</Descriptions.Item>
                <Descriptions.Item label="Thai Nickname">{selectedEmployee.thai_nickname || 'N/A'}</Descriptions.Item>
              </Descriptions>
            </Card>

            {/* Contact Information Section */}
            <Card
              title={<Space><PhoneOutlined /> Contact Information</Space>}
              style={{ marginBottom: 24 }}
              headStyle={{ backgroundColor: '#17a2b8', color: 'white' }}
            >
              <Row gutter={24}>
                <Col xs={24} md={12}>
                  <Descriptions bordered column={1} size="small">
                    <Descriptions.Item label="Phone Number">{selectedEmployee.phone_number || 'N/A'}</Descriptions.Item>
                    <Descriptions.Item label="Email">{selectedEmployee.email || 'N/A'}</Descriptions.Item>
                    <Descriptions.Item label="Line ID">{selectedEmployee.line_id || 'N/A'}</Descriptions.Item>
                  </Descriptions>
                </Col>
                <Col xs={24} md={12}>
                  <Card 
                    size="small" 
                    title="Emergency Contact"
                    headStyle={{ backgroundColor: '#dc3545', color: 'white' }}
                  >
                    <Descriptions bordered column={1} size="small">
                      <Descriptions.Item label="Contact Name">{selectedEmployee.emergency_contact_name || 'N/A'}</Descriptions.Item>
                      <Descriptions.Item label="Contact Phone">{selectedEmployee.emergency_contact_phone || 'N/A'}</Descriptions.Item>
                      <Descriptions.Item label="Relationship">{selectedEmployee.emergency_contact_relationship || 'N/A'}</Descriptions.Item>
                    </Descriptions>
                  </Card>
                </Col>
              </Row>
            </Card>

            {/* Address Information Section */}
            <Card
              title={<Space><HomeOutlined /> Address Information</Space>}
              style={{ marginBottom: 24 }}
              headStyle={{ backgroundColor: '#28a745', color: 'white' }}
            >
              <Row gutter={24}>
                <Col xs={24} md={12}>
                  <Card size="small" title="Current Address" style={{ marginBottom: 16 }}>
                    <Descriptions bordered column={1} size="small">
                      <Descriptions.Item label="Address">{selectedEmployee.current_address || 'N/A'}</Descriptions.Item>
                      <Descriptions.Item label="Sub District">{selectedEmployee.current_sub_district || 'N/A'}</Descriptions.Item>
                      <Descriptions.Item label="District">{selectedEmployee.current_district || 'N/A'}</Descriptions.Item>
                      <Descriptions.Item label="Province">{selectedEmployee.current_province || 'N/A'}</Descriptions.Item>
                      <Descriptions.Item label="Postal Code">{selectedEmployee.current_postal_code || 'N/A'}</Descriptions.Item>
                    </Descriptions>
                  </Card>
                </Col>
                <Col xs={24} md={12}>
                  <Card size="small" title="Permanent Address">
                    <Descriptions bordered column={1} size="small">
                      <Descriptions.Item label="Address">{selectedEmployee.permanent_address || 'N/A'}</Descriptions.Item>
                      <Descriptions.Item label="Province">{selectedEmployee.permanent_province || 'N/A'}</Descriptions.Item>
                      <Descriptions.Item label="Postal Code">{selectedEmployee.permanent_postal_code || 'N/A'}</Descriptions.Item>
                    </Descriptions>
                  </Card>
                </Col>
              </Row>
            </Card>

            {/* Employment Details & Identification Section */}
            <Card
              title={<Space><IdcardOutlined /> Employment Details & Identification</Space>}
              style={{ marginBottom: 24 }}
              headStyle={{ backgroundColor: '#ffc107', color: '#000' }}
            >
              <Row gutter={24}>
                <Col xs={24} md={12}>
                  <Card size="small" title="Employment Details" style={{ marginBottom: 16 }}>
                    <Descriptions bordered column={1} size="small">
                      <Descriptions.Item label="Employment Type">{selectedEmployee.employment_type || 'N/A'}</Descriptions.Item>
                      <Descriptions.Item label="Probation End Date">
                        {selectedEmployee.probation_end_date ? new Date(selectedEmployee.probation_end_date).toLocaleDateString('en-GB') : 'N/A'}
                      </Descriptions.Item>
                      <Descriptions.Item label="Salary Amount">
                        <span style={{ color: '#52c41a', fontWeight: 'bold' }}>
                          {selectedEmployee.salary_amount ? `THB ${parseFloat(selectedEmployee.salary_amount).toLocaleString()}` : 'N/A'}
                        </span>
                      </Descriptions.Item>
                      <Descriptions.Item label="Salary Type">{selectedEmployee.salary_type || 'N/A'}</Descriptions.Item>
                    </Descriptions>
                  </Card>
                </Col>
                <Col xs={24} md={12}>
                  <Card size="small" title="Identification & Banking">
                    <Descriptions bordered column={1} size="small">
                      <Descriptions.Item label="Thai ID Number">{selectedEmployee.thai_id_number || 'N/A'}</Descriptions.Item>
                      <Descriptions.Item label="Passport Number">{selectedEmployee.passport_number || 'N/A'}</Descriptions.Item>
                      <Descriptions.Item label="Tax ID">{selectedEmployee.tax_id || 'N/A'}</Descriptions.Item>
                      <Descriptions.Item label="Social Security Number">{selectedEmployee.social_security_number || 'N/A'}</Descriptions.Item>
                      <Descriptions.Item label="Bank Name">{selectedEmployee.bank_name || 'N/A'}</Descriptions.Item>
                      <Descriptions.Item label="Bank Branch">{selectedEmployee.bank_branch || 'N/A'}</Descriptions.Item>
                      <Descriptions.Item label="Account Number">{selectedEmployee.bank_account_number || 'N/A'}</Descriptions.Item>
                      <Descriptions.Item label="Account Name">{selectedEmployee.bank_account_name || 'N/A'}</Descriptions.Item>
                    </Descriptions>
                  </Card>
                </Col>
              </Row>
            </Card>

            {/* Visa & Immigration Details Section */}
            <Card
              title={<Space><FileProtectOutlined /> Visa & Immigration Details</Space>}
              style={{ marginBottom: 24 }}
              headStyle={{ backgroundColor: '#1890ff', color: 'white' }}
            >
              <Row gutter={24}>
                <Col xs={24} md={12}>
                  <Card size="small" title="Visa Information" style={{ marginBottom: 16 }}>
                    <Descriptions bordered column={1} size="small">
                      <Descriptions.Item label="Visa Type">{selectedEmployee.visa_type || 'N/A'}</Descriptions.Item>
                      <Descriptions.Item label="Visa Expiry Date">
                        <span style={{
                          fontWeight: 'bold',
                          color: selectedEmployee.visa_expiry && new Date(selectedEmployee.visa_expiry) < new Date() ? '#ff4d4f' : '#52c41a'
                        }}>
                          {selectedEmployee.visa_expiry ? new Date(selectedEmployee.visa_expiry).toLocaleDateString('en-GB') : 'N/A'}
                        </span>
                      </Descriptions.Item>
                      <Descriptions.Item label="Visa Status">
                        <Tag color={selectedEmployee.visa_expiry && new Date(selectedEmployee.visa_expiry) > new Date() ? 'success' : 'error'}>
                          {selectedEmployee.visa_expiry 
                            ? (new Date(selectedEmployee.visa_expiry) > new Date() ? 'Valid' : 'Expired')
                            : 'N/A'
                          }
                        </Tag>
                      </Descriptions.Item>
                    </Descriptions>
                  </Card>
                </Col>
                <Col xs={24} md={12}>
                  <Card size="small" title="Work Permit">
                    <Descriptions bordered column={1} size="small">
                      <Descriptions.Item label="Work Permit Number">{selectedEmployee.work_permit_number || 'N/A'}</Descriptions.Item>
                      <Descriptions.Item label="Work Permit Expiry">
                        <span style={{
                          fontWeight: 'bold',
                          color: selectedEmployee.work_permit_expiry && new Date(selectedEmployee.work_permit_expiry) < new Date() ? '#ff4d4f' : '#52c41a'
                        }}>
                          {selectedEmployee.work_permit_expiry ? new Date(selectedEmployee.work_permit_expiry).toLocaleDateString('en-GB') : 'N/A'}
                        </span>
                      </Descriptions.Item>
                      <Descriptions.Item label="Work Permit Status">
                        <Tag color={selectedEmployee.work_permit_expiry && new Date(selectedEmployee.work_permit_expiry) > new Date() ? 'success' : 'error'}>
                          {selectedEmployee.work_permit_expiry 
                            ? (new Date(selectedEmployee.work_permit_expiry) > new Date() ? 'Valid' : 'Expired')
                            : 'N/A'
                          }
                        </Tag>
                      </Descriptions.Item>
                    </Descriptions>
                  </Card>
                </Col>
              </Row>
              <Row gutter={24} style={{ marginTop: 16 }}>
                <Col span={24}>
                  <Card size="small" title="Additional Immigration Information">
                    <Descriptions bordered column={2} size="small">
                      <Descriptions.Item label="Passport Number">{selectedEmployee.passport_number || 'N/A'}</Descriptions.Item>
                      <Descriptions.Item label="Nationality">{selectedEmployee.nationality || 'N/A'}</Descriptions.Item>
                    </Descriptions>
                  </Card>
                </Col>
              </Row>
            </Card>

            {/* Additional Notes Section */}
            {selectedEmployee.notes && (
              <Card
                title="Additional Notes"
                style={{ marginBottom: 24 }}
              >
                <p style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{selectedEmployee.notes}</p>
              </Card>
            )}

          </div>
        )}
      </Modal>

      {/* Bulk Upload Modal */}
      <Modal
        title={
          <span>
            <UploadOutlined style={{ marginRight: 8 }} />
            Bulk Employee Upload
          </span>
        }
        open={showBulkUpload}
        onCancel={() => {
          setShowBulkUpload(false);
          setUploadProgress(0);
          setUploadResults(null);
        }}
        footer={!isUploading ? [
          <Button 
            key="close" 
            onClick={() => {
              setShowBulkUpload(false);
              setUploadProgress(0);
              setUploadResults(null);
            }}
          >
            Close
          </Button>
        ] : null}
        width={800}
        centered
      >
        {!isUploading && !uploadResults && (
          <>
            <Alert
              message="Instructions"
              description={
                <ul style={{ marginBottom: 0, paddingLeft: 20 }}>
                  <li>Download the sample CSV format file first</li>
                  <li>Fill in your employee data following the exact column headers</li>
                  <li>Make sure all dates are in YYYY-MM-DD format</li>
                  <li>Employee IDs should be unique</li>
                  <li>Required fields: employee_id, english_first_name, english_last_name</li>
                  <li>Upload the completed CSV file (max file size: 10MB)</li>
                  <li>The system will automatically validate and process your data</li>
                </ul>
              }
              type="info"
              showIcon
              icon={<InfoCircleOutlined />}
              style={{ marginBottom: 24 }}
            />

            <Row gutter={16} style={{ marginBottom: 24 }}>
              <Col span={12}>
                <Button
                  type="primary"
                  icon={<DownloadOutlined />}
                  onClick={downloadSampleCSV}
                  block
                  size="large"
                  style={{ height: '50px', fontSize: '16px' }}
                >
                  Download Sample Format
                </Button>
              </Col>
              <Col span={12}>
                <Upload
                  accept=".csv"
                  beforeUpload={(file) => {
                    handleBulkUpload({ target: { files: [file] } });
                    return false;
                  }}
                  showUploadList={false}
                >
                  <Button
                    type="default"
                    icon={<UploadOutlined />}
                    block
                    size="large"
                    style={{ height: '50px', fontSize: '16px' }}
                  >
                    Select CSV File to Upload
                  </Button>
                </Upload>
              </Col>
            </Row>
          </>
        )}

        {isUploading && (
          <div style={{ textAlign: 'center', padding: '40px 20px' }}>
            <LoadingOutlined style={{ fontSize: 48, color: '#1890ff', marginBottom: 16 }} spin />
            <h4 style={{ marginBottom: 24 }}>Processing CSV File...</h4>
            <Progress
              percent={Math.round(uploadProgress)}
              status="active"
              strokeColor={{
                '0%': '#108ee9',
                '100%': '#87d068',
              }}
              style={{ marginBottom: 16 }}
            />
            <p style={{ color: '#8c8c8c' }}>
              {uploadProgress < 20 ? 'Uploading file to server...' :
               uploadProgress < 90 ? 'Server is processing your CSV data...' :
               'Finalizing upload...'}
            </p>
          </div>
        )}

        {uploadResults && (
          <div>
            <Alert
              message="Upload Complete!"
              type="success"
              showIcon
              icon={<CheckCircleOutlined />}
              style={{ marginBottom: 24 }}
            />
            
            <Row gutter={16} style={{ marginBottom: 24 }}>
              <Col span={8}>
                <Statistic
                  title="Total Records"
                  value={uploadResults.total}
                  prefix={<InfoCircleOutlined style={{ color: '#1890ff' }} />}
                  valueStyle={{ color: '#1890ff' }}
                />
              </Col>
              <Col span={8}>
                <Statistic
                  title="Successful"
                  value={uploadResults.successful}
                  prefix={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
                  valueStyle={{ color: '#52c41a' }}
                />
              </Col>
              <Col span={8}>
                <Statistic
                  title="Failed"
                  value={uploadResults.failed}
                  prefix={<CloseCircleOutlined style={{ color: '#ff4d4f' }} />}
                  valueStyle={{ color: '#ff4d4f' }}
                />
              </Col>
            </Row>

            {uploadResults.errors.length > 0 && (
              <>
                <Divider />
                <Alert
                  message="Upload Errors"
                  type="warning"
                  showIcon
                  style={{ marginBottom: 16 }}
                />
                <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                  <List
                    size="small"
                    dataSource={uploadResults.errors}
                    renderItem={(error, index) => (
                      <List.Item key={index}>
                        <List.Item.Meta
                          avatar={<CloseCircleOutlined style={{ color: '#ff4d4f' }} />}
                          title={`Row ${error.row} (${error.employee_id})`}
                          description={error.error}
                        />
                      </List.Item>
                    )}
                    bordered
                  />
                </div>
              </>
            )}
          </div>
        )}
      </Modal>
    </>
  );
}
